import { useCallback, useEffect, useRef, useState } from "react";
import merchantApi from "../../../lib/merchantApi";

/**
 * Debounced auto-save hook for an onboarding step.
 *
 * STRICT RULE (Feb 2026 — see CONTRIBUTING.md "Save responses never write to
 * user-editable state"). The success callback `onSaveMeta` is intentionally
 * narrow: it receives ONLY server-owned metadata (status, ref, step_status,
 * last_step, schemas, application_id, last_saved_at). It MUST NOT include
 * `step_data` — that is user-editable field state and is the local source of
 * truth during a typing session. The only place server `step_data` populates
 * fields is the initial bootstrap fetch in OnboardingPage.
 *
 * Other guarantees:
 * - Debounce 600 ms after last change.
 * - In-flight aborts: when a new flush starts, the previous request is
 *   aborted via AbortController so its (stale-snapshot) response can never
 *   reach `onSaveMeta`.
 * - Last-write-wins. We never throw a conflict to the user.
 * - Silent retry on transient failures (3 attempts with backoff). 4xx errors
 *   are surfaced as "error" state but the user's input is never reverted.
 */

// Whitelist of metadata keys the autosave callback is allowed to forward to
// the parent. Anything else (notably `step_data`) is dropped.
const META_KEYS = [
  "application_id", "ref", "status", "step_status",
  "last_step", "schemas", "last_saved_at",
];

function pickMeta(data) {
  if (!data || typeof data !== "object") return {};
  const out = {};
  for (const k of META_KEYS) {
    if (k in data) out[k] = data[k];
  }
  return out;
}

export default function useAutoSave(stepNum, onSaveMeta) {
  const [savingState, setSavingState] = useState("idle"); // idle | saving | saved | error
  const pendingRef = useRef({});                           // accumulated patch
  const flushTimerRef = useRef(null);
  const inflightControllerRef = useRef(null);

  const flush = useCallback(async () => {
    const patch = pendingRef.current;
    if (!patch || Object.keys(patch).length === 0) return;
    pendingRef.current = {};

    // Abort the previous in-flight request, if any. Its response (built from
    // a stale snapshot of `step_data`) must never reach the callback.
    if (inflightControllerRef.current) {
      try { inflightControllerRef.current.abort(); } catch { /* noop */ }
    }
    const controller = new AbortController();
    inflightControllerRef.current = controller;
    setSavingState("saving");

    let attempt = 0;
    let lastErr = null;
    while (attempt < 3) {
      try {
        const { data } = await merchantApi.put(
          `/merchant/application/step/${stepNum}`,
          { data: patch },
          { signal: controller.signal },
        );
        // If we were aborted between request send and response, drop on the
        // floor. The newer flush owns the success path.
        if (controller.signal.aborted) return;
        setSavingState("saved");
        // STRICT: forward metadata only — never touch user-editable fields.
        onSaveMeta?.(pickMeta(data));
        if (inflightControllerRef.current === controller) {
          inflightControllerRef.current = null;
        }
        return;
      } catch (err) {
        // Aborted? Stop retrying — a newer flush has taken over.
        if (err?.name === "CanceledError" || err?.name === "AbortError"
            || controller.signal.aborted) return;
        lastErr = err;
        attempt += 1;
        await new Promise((r) => setTimeout(r, [250, 750, 1500][attempt - 1] || 1500));
      }
    }
    // After 3 attempts we still don't surface a hard error to the user — we
    // keep the patch in pending so the next change will retry it. CRITICAL:
    // we do NOT touch `localStepData` here — the user's input remains the
    // source of truth.
    pendingRef.current = { ...patch, ...pendingRef.current };
    if (inflightControllerRef.current === controller) {
      inflightControllerRef.current = null;
    }
    setSavingState("error");
    // eslint-disable-next-line no-console
    console.warn("[onboarding] auto-save retry exhausted, will retry on next change", lastErr);
  }, [stepNum, onSaveMeta]);

  /** Queue a partial change, debounced. */
  const queueSave = useCallback((patch) => {
    pendingRef.current = { ...pendingRef.current, ...patch };
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(flush, 600);
  }, [flush]);

  /** Force flush — used on blur, on Continue, on unmount. */
  const flushNow = useCallback(() => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    return flush();
  }, [flush]);

  useEffect(() => () => {
    // Best-effort flush on unmount; abort any in-flight on teardown.
    if (flushTimerRef.current) flush();
    if (inflightControllerRef.current) {
      try { inflightControllerRef.current.abort(); } catch { /* noop */ }
    }
  }, [flush]);

  return { savingState, queueSave, flushNow };
}
